import * as React from 'react';
import { useIntl } from 'react-intl';

import { useQuery } from '@apollo/client';

import { DrawerContext, Grid, ModalContext } from '@notino/react-styleguide';
import { getImageAttributes } from '@notino/react-web-utils';
import {
  GetRichContentForReviewsPageQuery,
  GetRichContentForReviewsPageQueryVariables,
  RichContentTargetPage,
} from '@notino/shared/definitions/types';

import { ContentWrapper } from '@components';
import { DrawerProvider } from '@components/BottomSheet/DrawerProvider';
import { Divider } from '@components/Divider';
import ErrorBoundary from '@components/ErrorBoundary';
import { PageGone } from '@components/NotFound';
import { ProductQueryParams } from '@containers/ProductDetailContainer';
import { useGetProductView } from '@containers/ProductDetailContainer/hooks/useGetProductView';
import { useSettings } from '@containers/ProductDetailContainer/hooks/useSettings';
import { Breadcrumb } from '@containers/ProductDetailContainer/ProductDetail/Breadcrumb';
import { useShouldShowReviewPage } from '@containers/ProductDetailContainer/ProductDetail/hooks/useShouldShowReviewPage';
import { useTrackLoyaltyStatus } from '@containers/ProductDetailContainer/ProductDetail/hooks/useTrackLoyaltyStatus';
import { getProductFullName } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/utils';
import {
  ProductDetailContextProvider,
  useProductDetailContext,
} from '@containers/ProductDetailContainer/ProductDetailContext';
import { ProductStateHandler } from '@containers/ProductDetailContainer/ProductStateHandler';
import { dispatchTrackingEvent } from '@context/tracking/utils';

import { DESCRIPTION_MIN_LENGTH } from './constants';
import { Header } from './Header';
import { HeadMetaReviewsPage } from './HeadMeta/HeadMetaReviewsPage';
import { headMetaMessages } from './HeadMeta/messages';
import { Reviews } from './Reviews';
import { RichContentSection } from './RichContentSection';
import getRichContentQuery from './RichContentSection/richContent.graphql';
import { ReviewsPageWrapper } from './styled';
import { findDescriptionWithoutStrong, getProductName } from './utils';

const ReviewsPage = () => {
  const { product, currentVariant, currentCurrency } =
    useProductDetailContext();
  const { Settings: { imageDomainPath } = {} } = useSettings();
  const { formatMessage } = useIntl();

  const shouldShowReviewPage = useShouldShowReviewPage(
    product.reviewOverview?.count ?? 0,
    product.reviewOverview?.rating ?? 0
  );

  useTrackLoyaltyStatus({ skip: !shouldShowReviewPage });

  const { data } = useQuery<
    GetRichContentForReviewsPageQuery,
    GetRichContentForReviewsPageQueryVariables
  >(getRichContentQuery, {
    variables: {
      catalogMasterId: product.catalogMasterId,
      targetPage: RichContentTargetPage.ProductReviews,
    },
    ssr: true,
    skip: !shouldShowReviewPage,
  });

  React.useEffect(() => {
    dispatchTrackingEvent({
      event: 'page_view',
      page: {
        path: window.location.pathname,
        title: formatMessage(headMetaMessages.seoTitle, {
          productName: getProductName(currentVariant, product),
        }),
        type: 'review',
        unified_name: `product-${currentVariant.productCode ?? ''}`,
        subpage: undefined,
      },
      _clear: true,
    });
  }, [formatMessage, product, currentVariant]);

  const productName = getProductName(currentVariant, product);
  const productFullName = getProductFullName(product);

  const annotation =
    currentVariant.attributes && currentVariant.attributes.Master
      ? product.annotation
      : currentVariant.variantName;

  const imageAttrs = getImageAttributes(
    currentVariant.media?.mainImage as any,
    imageDomainPath ?? '',
    'detailMain'
  );

  const breadcrumbs = [
    ...(product.breadcrumb ?? []),
    {
      name: product.brand?.name ?? '',
      link: product.brand?.url ?? '',
    },
    {
      name: getProductName(currentVariant, product),
      link: product.url ?? '',
    },
  ];

  const description = findDescriptionWithoutStrong(product.description ?? '');

  const simpleDescription =
    description.length > DESCRIPTION_MIN_LENGTH ? description : null;

  if (!shouldShowReviewPage) {
    return <PageGone />;
  }

  return (
    <ReviewsPageWrapper>
      <ErrorBoundary>
        <HeadMetaReviewsPage
          simpleDescription={simpleDescription}
          product={product}
          variant={currentVariant}
        />
      </ErrorBoundary>

      <ErrorBoundary>
        <ContentWrapper>
          <Grid fluid={true}>
            <Breadcrumb items={breadcrumbs as any} />

            <Header
              simpleDescription={simpleDescription}
              imageAttrs={imageAttrs}
              annotation={annotation ?? ''}
              productFullName={productFullName}
              productName={productName}
              redirectUrl={product.url ?? ''}
              selectedVariant={currentVariant}
              currency={currentCurrency as any}
              hasRichContent={
                data?.RichContentByCatalogMasterId?.isAvailable ?? false
              }
            />

            <Divider />

            <Reviews
              productCode={product.masterProductCode}
              masterId={product.webMasterId}
              isAllReviewsPage={true}
              product={product}
              imageDomainPath={imageDomainPath ?? ''}
              selectedVariant={currentVariant}
            />

            <RichContentSection data={data as any} />
          </Grid>
        </ContentWrapper>
      </ErrorBoundary>
    </ReviewsPageWrapper>
  );
};

const WrappedReviewsPage: React.FC<ProductQueryParams> = ({
  catalogId,
  ssrEnabled,
}) => {
  const { data, error, loading } = useGetProductView({
    catalogId,
    ssrEnabled,
  });

  return (
    <ProductStateHandler
      data={data}
      error={error}
      loading={loading}
      isReviewPage={true}
    >
      {/* the nullish data scenario is handled in ProductStateHandler */}
      <ProductDetailContextProvider PDView={data!}>
        <ModalContext.Provider>
          <DrawerContext.Provider>
            <DrawerProvider>
              <ReviewsPage />
            </DrawerProvider>
          </DrawerContext.Provider>
        </ModalContext.Provider>
      </ProductDetailContextProvider>
    </ProductStateHandler>
  );
};

export default WrappedReviewsPage;
